import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Play, Save, Mic } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

const MODELS = [["eleven_multilingual_v2", "Multilingual v2 (current)"], ["eleven_v3", "Eleven v3"], ["eleven_v4", "Eleven v4 (newest)"]];
const call = (path, opts = {}) => fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...authHeader() } });

function usePreview() {
  const audio = useRef(null);
  const [busy, setBusy] = useState(null);
  const play = async (id, text) => {
    setBusy(id);
    try {
      const res = await call("/admin/podcast/pronounce-preview", { method: "POST", body: JSON.stringify({ text }) });
      if (!res.ok) throw new Error("Preview failed");
      audio.current?.pause();
      audio.current = new Audio(URL.createObjectURL(await res.blob()));
      await audio.current.play();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  return { play, busy };
}

function Row({ e, i, onChange, onDelete, preview }) {
  return (
    <tr className="border-b border-sand-dark last:border-0" data-testid={`lex-row-${i}`}>
      <td className="py-2 pr-2"><input value={e.word} onChange={(ev) => onChange({ ...e, word: ev.target.value })} data-testid={`lex-word-${i}`}
        className="w-full h-9 px-2 border border-sand-dark rounded-sm text-sm" placeholder="Tengboche" /></td>
      <td className="py-2 pr-2"><input value={e.say} onChange={(ev) => onChange({ ...e, say: ev.target.value })} data-testid={`lex-say-${i}`}
        className="w-full h-9 px-2 border border-sand-dark rounded-sm text-sm font-mono" placeholder="Tengbochay" /></td>
      <td className="py-2 whitespace-nowrap">
        <button onClick={() => preview.play(i, `We stay in ${e.word}.`)} disabled={!e.word || preview.busy !== null} data-testid={`lex-play-${i}`}
          className="h-9 w-9 inline-flex items-center justify-center rounded-sm hover:bg-sand" title="Hear Raj say it (saves first)">
          {preview.busy === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}</button>
        <button onClick={onDelete} data-testid={`lex-delete-${i}`} className="h-9 w-9 inline-flex items-center justify-center rounded-sm hover:bg-sand text-terracotta"><Trash2 className="w-4 h-4" /></button>
      </td>
    </tr>
  );
}

export default function VoiceAdmin() {
  const [vs, setVs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState("everest-base-camp-wellness-adventure");
  const preview = usePreview();
  useEffect(() => { call("/admin/podcast/voice-settings").then((r) => r.json()).then(setVs); }, []);

  const save = async (quiet) => {
    setSaving(true);
    const res = await call("/admin/podcast/voice-settings", { method: "PUT", body: JSON.stringify(vs) });
    setSaving(false);
    if (res.ok) { setVs(await res.json()); if (!quiet) toast.success("Pronunciations saved"); } else toast.error("Save failed");
    return res.ok;
  };
  const previewRow = { ...preview, play: async (i, t) => (await save(true)) && preview.play(i, t) };
  const revoice = async () => {
    if (!(await save(true))) return;
    const res = await call(`/admin/tours/${slug}/podcast/revoice`, { method: "POST" });
    res.ok ? toast.success("Re-recording the podcast. It takes about 2 minutes; then play it on the tour page.") : toast.error((await res.json()).detail || "Failed");
  };
  const setLex = (lexicon) => setVs({ ...vs, lexicon });

  if (!vs) return <Loader2 className="w-5 h-5 animate-spin text-moss" />;
  return (
    <div className="max-w-3xl space-y-6" data-testid="voice-admin">
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight">Raj's podcast voice</h2>
        <p className="text-sm text-ink-secondary mt-1">How names are spoken in the "What to expect" podcasts. "Say it as" is used only for the audio; transcripts keep the real spelling. Tap play to hear Raj say a word.</p>
      </div>
      <label className="block text-sm font-medium">Voice model
        <select value={vs.model_id} onChange={(e) => setVs({ ...vs, model_id: e.target.value })} data-testid="voice-model-select"
          className="mt-1 block h-10 px-3 border border-sand-dark rounded-sm bg-white text-sm">
          {MODELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <table className="w-full bg-white border border-sand-dark rounded-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-ink-secondary"><th className="p-2">Word</th><th className="p-2">Say it as</th><th /></tr></thead>
        <tbody className="[&_td:first-child]:pl-2">
          {vs.lexicon.map((e, i) => <Row key={i} e={e} i={i} preview={previewRow}
            onChange={(n) => setLex(vs.lexicon.map((x, j) => (j === i ? n : x)))} onDelete={() => setLex(vs.lexicon.filter((_, j) => j !== i))} />)}
        </tbody>
      </table>
      <div className="flex gap-3 flex-wrap">
        <button onClick={() => setLex([...vs.lexicon, { word: "", say: "" }])} data-testid="lex-add" className="h-10 px-4 rounded-sm border border-sand-dark text-sm font-medium flex items-center gap-1.5 hover:bg-sand"><Plus className="w-4 h-4" /> Add word</button>
        <button onClick={() => save()} disabled={saving} data-testid="lex-save" className="h-10 px-4 rounded-sm bg-moss text-white text-sm font-semibold flex items-center gap-1.5">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
      </div>
      <div className="bg-sand/50 border border-sand-dark rounded-sm p-4 space-y-3">
        <p className="text-sm font-medium flex items-center gap-2"><Mic className="w-4 h-4" /> Re-record a podcast with these settings (same script)</p>
        <div className="flex gap-3 flex-wrap">
          <input value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="revoice-slug" className="flex-1 min-w-[240px] h-10 px-3 border border-sand-dark rounded-sm text-sm font-mono" />
          <button onClick={revoice} data-testid="revoice-btn" className="h-10 px-4 rounded-sm bg-terracotta text-white text-sm font-semibold">Re-record podcast</button>
        </div>
      </div>
    </div>
  );
}
